import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {ExperimentConditionREST} from 'procedures/network/ProcedureRunDAO';
import {FullExperimentPlan} from 'procedures/planning/ProcedurePlanTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

const MATERIAL_INDEX = 0;
const AMOUNT_INDEX = 1;
const VALUE_INDEX = 0; // parameters and equipment currently only have one field, so we use this index for both

/**
 * If TypedValue has at least one sub-value, returns the unnested array of values.
 * Otherwise, returns a single element array with the given TypedValue.
 *
 * This should be used on multi-value parameter fields. On the frontend, a multi-value field is represented as
 * a single multilink TypedValue of N values. For the backend, it needs to be expanded into N single-link
 * TypedValues for N ParameterValue objects.
 */
function maybeExpandTypedValueForMultiValuePlateMap(typedValue: TypedValue): Array<TypedValue> {
  let values: Array<TypedValue> = [];
  if (typedValue.type === BaseTypedValueType.ARRAY) {
    values = typedValue.rawValue;
  } else if (typedValue.type === FieldType.BIOENTITY_LINK) {
    values = typedValue.targetFiles.map((targetFile) => ({
      ...typedValue,
      targetFiles: [targetFile],
    }));
  }
  // Always return at least one TypedValue, even if the ArrayTypedValue or BioentityLinkTypedValue is empty
  return values.length ? values : [typedValue];
}

export function serializeExperimentConditionsForNetwork(
  fullExperimentPlan: FullExperimentPlan
): Array<ExperimentConditionREST> {
  const {replicatesPerCondition, namesPerCondition, descriptionsPerCondition, experimentPlanPerCondition} =
    fullExperimentPlan;
  assert(
    replicatesPerCondition.length === experimentPlanPerCondition.length,
    'expected same number of conditions when tracking replicates and planned values'
  );
  return experimentPlanPerCondition.map((methodPlans, i) => ({
    replicateInfos: Array.from({length: replicatesPerCondition[i]}, (_, j) => ({
      replicateNumber: j + 1,
      sourcePMEIIdsByTemplatePMDVId: {},
    })),
    methodPlans: methodPlans.map((methodPlan) => ({
      methodVersionId: methodPlan.methodId,
      dataParameterValues: methodPlan.parameterValues.map((pv) => ({
        dataParameterVersionId: pv.sourceId,
        values: maybeExpandTypedValueForMultiValuePlateMap(pv.values[VALUE_INDEX]).map((typedValue) => ({
          cid: `planned_value-${uuidv4()}`,
          value: typedValue,
        })),
      })),
      materialInputValues: methodPlan.materialInputs.map((pv) => ({
        materialInputVersionId: pv.sourceId,
        values: maybeExpandTypedValueForMultiValuePlateMap(pv.values[MATERIAL_INDEX]).map(
          (materialTypedValue) => ({
            cid: `planned_value-${uuidv4()}`,
            material: materialTypedValue,
            amount: pv.values[AMOUNT_INDEX] ?? {type: 'ft_float', value: null},
          })
        ),
      })),
      equipmentValues: methodPlan.equipmentInputs.map((pv) => ({
        equipmentVersionId: pv.sourceId,
        values: [{cid: `planned_value-${uuidv4()}`, equipment: pv.values[VALUE_INDEX]}],
      })),
    })),
    name: namesPerCondition[i],
    description: descriptionsPerCondition?.[i] ?? undefined,
  }));
}
